import { Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';

import { DateMomentPipe } from '../../../../pipes';
import { AppStateHolder, TimeLine } from '../../../../shared';
import { UserActivitiesService } from '../user-activities.service';

@Component({
    selector: 'hour-chart',
    templateUrl: 'activity-hourChart.component.html',
    styleUrls: ['activity-hourChart.component.scss'],
    standalone: true,
    imports: [TooltipModule, DecimalPipe, DatePipe, TranslateModule, DateMomentPipe]
})

export class ActivityHourChartComponent implements OnInit, OnDestroy {
  private appHolder = inject(AppStateHolder);
  private timeLine = inject(TimeLine);
  private service = inject(UserActivitiesService);

  /** Reference to the accessible data table for focus management */
  @ViewChild('hourDataTable') hourDataTable: ElementRef<HTMLTableElement>;

  @Input() modify: Boolean;
  @Input() idx: number;
  @Input() border: boolean;
  @Input() params: string;
  @Input() changedParams: any;
  @Input() widgetPosition: any;

  @Output() dimensionSelected: EventEmitter<any> = new EventEmitter();
  @Output() popup: EventEmitter<{ widx: number, pop: boolean, params: any }> = new EventEmitter();

  requestsSubscribe: Subscription;
  status: string;
  activities: any[];
  openOptions: boolean = false;
  optionSelected: any;

  finalDate: string;
  initialDate: string;

  totalHours: number;
  closedSum: number;
  totalTime: number;
  esTime: number;
  eftTime: number;
  workedActivityTimeSum: number;
  totalActivityTimeSum: number;
  shortDate: any = [{ format: "L" }];

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
  }

  ngOnInit(): void {
    if (!this.modify) {
      this.timeLine.init();

      this.workedActivityTimeSum = 0;
      this.totalActivityTimeSum = 0;
      this.totalTime = 0;
      this.eftTime = 0;
      this.esTime = 0;

      this.loadActivities(this.params);
    }
  }

  loadActivities(params: string) {

    this.status = 'busy';

    if (params) {
      let prms = JSON.parse(params);

      prms = prms.params ? JSON.parse(prms.params) : prms;

      if (prms.date == 'Day')
        this.dataClick(0);
      if (prms.date == 'Week')
        this.dataClick(1);
      if (prms.date == 'Month')
        this.dataClick(2);
      if (prms.date == 'all')
        this.dataClick(1);

      this.requestsSubscribe = this.service.getUserActivitiesFil(this.initialDate, this.finalDate, prms.state, prms.typologies)
        .subscribe(res => {
          this.status = 'ready';
          this.activities = res;
          this.timeCalculation();
        })
    }
    else {
      this.dataClick(1);
      this.service
        .getUserActivitiesFil(this.initialDate, this.finalDate, null, null)
        .subscribe(result => {

          this.status = 'ready';
          this.activities = result;
          this.timeCalculation();
        });
    }
  }

  timeCalculation() {

    this.activities.forEach((r) => {
      if (r.effectiveTime != null && r.effectiveTime > 0) {
        this.totalActivityTimeSum += r.effectiveTime;
        if (r.workingState != 0) //open
          this.workedActivityTimeSum += r.effectiveTime;
      }
      else if (r.estimatedTime != null && r.estimatedTime > 0) {
        this.totalActivityTimeSum += r.estimatedTime;
        if (r.workingState != 0) //open
          this.workedActivityTimeSum += r.estimatedTime;
      }
    })
  }

  hasActivities(): Boolean {

    if (this.activities === undefined)
      return false;

    return (this.activities.length > 0);
  }

  options() {
    this.openOptions = !this.openOptions;
  }

  selected() {
    this.dimensionSelected.next(this.optionSelected);
  }

  actpop() {
    if (this.changedParams == undefined) {
      this.sendParams();
    }
    else {
      let val = this.appHolder.hourParams.filter(r => { return r.indx == this.idx });

      if (val.length > 0) {
        this.popup.next({ widx: this.idx, pop: true, params: JSON.parse(val[0].params) });
      }
      else {
        this.sendParams();
      }
    }
  }

  sendParams() {

    if (this.params) {
      this.popup.next({ widx: this.idx, pop: true, params: JSON.parse(this.params) });
    }
    else {
      this.popup.next({ widx: this.idx, pop: true, params: this.params });
    }
  }

  dataClick(dateType: number) {

    let day;
    let week;
    let month;

    switch (dateType) {
      case 0:

        day = this.timeLine.day.date;

        this.finalDate = this.initialDate = (day.getFullYear() + "-" + (day.getMonth() + 1) + "-" + day.getDate());
        break;
      case 1:

        week = this.timeLine.week;
        let d1 = week.days[0].date;
        let d2 = week.days[week.days.length - 1].date;

        this.initialDate = d1.getFullYear() + "-" + (d1.getMonth() + 1) + "-" + d1.getDate();
        this.finalDate = d2.getFullYear() + "-" + (d2.getMonth() + 1) + "-" + d2.getDate();

        break;

      case 2:

        this.initialDate = this.timeLine.month.date.getFullYear() + "-" + (this.timeLine.month.date.getMonth() + 1) + "-" + this.timeLine.month.date.getDate();
        this.finalDate = this.timeLine.month.lastDate.getFullYear() + "-" + (this.timeLine.month.lastDate.getMonth() + 1) + "-" + this.timeLine.month.lastDate.getDate();

        break;

      case 3:

        this.initialDate = null;
        this.finalDate = null;

        break;
    }
  }

  ngOnDestroy(): void {
    if (this.requestsSubscribe)
      this.requestsSubscribe.unsubscribe()
  }

  /**
   * Focuses the accessible data table for screen reader users.
   * This method is called when users activate the skip link.
   */
  focusDataTable(): void {
    if (this.hourDataTable?.nativeElement) {
      this.hourDataTable.nativeElement.focus();
    }
  }
}

